import {
  BEATS,
  FILM_END,
  TIMING,
  assert,
  createDirectSystemFilm,
  framePlacement,
  getCurrentPlayer,
  installPreparedJourney,
  nearestFrame,
  releasePreparedJourney,
  validateJourney
} from "./bb-system-progressive-chunk-5OCLGI67.js";

// runtime/system-progressive/after-paint.js
function afterFirstPaint(signal) {
  return new Promise((resolve, reject) => {
    let frame = 0;
    const abort = () => {
      cancelAnimationFrame(frame);
      signal?.removeEventListener("abort", abort);
      reject(signal?.reason ?? Error("Canceled"));
    };
    if (signal?.aborted) {
      abort();
      return;
    }
    signal?.addEventListener("abort", abort, { once: true });
    frame = requestAnimationFrame(() => {
      frame = requestAnimationFrame(() => {
        signal?.removeEventListener("abort", abort);
        resolve();
      });
    });
  });
}

// runtime/system-progressive/readiness.js
function createReadiness({ createPreparation: createPreparation2, options, settlePosters, waitVisible: waitVisible2, afterPaint = async () => {
}, onPrepared, onState = () => {
}, onError = () => {
}, saveData = false, supported = true }) {
  let state = { phase: "idle", attempt: 0 }, task = null, pack = null, disposed = false, controller = new AbortController();
  const set = (phase, detail = {}) => {
    state = { ...state, ...detail, phase };
    onState({ ...state });
  };
  const start = ({ explicit = false } = {}) => {
    if (task || disposed || ["prepared", "failed", "unsupported"].includes(state.phase)) return task ?? Promise.resolve();
    if (saveData && !explicit) {
      set("declined");
      return Promise.resolve();
    }
    if (!supported) {
      set("unsupported", { error: "Direct decoding unavailable" });
      return Promise.resolve();
    }
    task = (async () => {
      try {
        await waitVisible2(controller.signal);
        await afterPaint(controller.signal);
        Promise.resolve(settlePosters(controller.signal)).catch(() => {
        });
        if (controller.signal.aborted) throw controller.signal.reason;
        set("fetching", { attempt: 1 });
        for (let attempt = 1; attempt <= 2; attempt++) {
          state.attempt = attempt;
          try {
            pack = await createPreparation2({ ...options, signal: controller.signal, onProgress: (progress) => set(progress.phase === "priming" ? "priming" : "fetching", progress) });
            break;
          } catch (error) {
            if (controller.signal.aborted || error.code === "capability" || attempt === 2 || !/HTTP|SHA|fetch|network|Metadata/i.test(error.message)) throw error;
            set("fetching", { attempt: 2, retry: error.message });
          }
        }
        if (pack.earlyStart) {
          await onPrepared(pack, controller.signal);
          if (controller.signal.aborted) throw controller.signal.reason;
        }
        await pack.prepare(controller.signal, (signal) => waitVisible2(signal ?? controller.signal));
        if (controller.signal.aborted) throw controller.signal.reason;
        if (!pack.earlyStart) await onPrepared(pack, controller.signal);
        if (controller.signal.aborted) throw controller.signal.reason;
        set("prepared", { bytes: pack.bound.totalBytes });
      } catch (error) {
        pack?.dispose();
        pack = null;
        if (!disposed) {
          set(error.code === "capability" ? "unsupported" : "failed", { error: error.message });
          onError(error);
        }
      }
    })();
    return task;
  };
  return { start, snapshot: () => ({ ...state }), get pack() {
    return pack;
  }, dispose() {
    if (disposed) return;
    disposed = true;
    controller.abort(Error("System section removed"));
    pack?.dispose();
    pack = null;
    set("disposed");
  } };
}

// runtime/system-motion/temporal-engine.js
var fail = (ok, message) => {
  if (!ok) throw Error(message);
};
var sha256 = async (bytes) => Array.from(new Uint8Array(await crypto.subtle.digest("SHA-256", bytes)), (n) => n.toString(16).padStart(2, "0")).join("");
var ResourceBudget = class {
  constructor(cap = 96 * 1024 * 1024) {
    this.cap = cap;
    this.resident = 0;
    this.transfers = 0;
    this.submitted = 0;
    this.peak = 0;
  }
  add(kind, n) {
    fail(this[kind] + n >= 0, "Negative resource ledger");
    fail(this.resident + this.transfers + this.submitted + n <= this.cap, "Compressed ledger cap exceeded");
    this[kind] += n;
    this.peak = Math.max(this.peak, this.resident + this.transfers + this.submitted);
  }
  transferToResident(bytes) {
    fail(this.transfers >= bytes, "Missingtransferreservation");
    this.transfers -= bytes;
    this.resident += bytes;
  }
  snapshot() {
    return { cap: this.cap, resident: this.resident, transfers: this.transfers, submitted: this.submitted, peak: this.peak };
  }
};
var FramePool = class {
  constructor(cap = 8) {
    this.cap = cap;
    this.frames = /* @__PURE__ */ new Map();
    this.reserved = 0;
    this.peakFrames = 0;
    this.peakOutstanding = 0;
    this.peakCombined = 0;
    this.closed = 0;
    this.engines = /* @__PURE__ */ new Set();
    this.target = 604;
    this.observer = null;
  }
  key(engine, logical) {
    return engine.id + ":" + logical;
  }
  get(engine, logical) {
    return this.frames.get(this.key(engine, logical))?.frame ?? null;
  }
  list(engine) {
    return [...this.frames.values()].filter((r) => !engine || r.engine === engine);
  }
  drop(key) {
    const r = this.frames.get(key);
    if (r) {
      r.frame.close();
      this.frames.delete(key);
      this.closed++;
    }
  }
  clear(engine) {
    for (const [key, r] of this.frames) if (!engine || r.engine === engine) this.drop(key);
  }
  reserve(n, target) {
    if (this.reserved + n > this.cap) return false;
    this.target = target;
    while (this.frames.size + this.reserved + n > this.cap) {
      const far = [...this.frames].sort((a, b) => Math.abs(b[1].logical - target) - Math.abs(a[1].logical - target) || a[1].logical - b[1].logical)[0];
      fail(far, "No eviction candidate");
      this.drop(far[0]);
    }
    this.reserved += n;
    this.track();
    return true;
  }
  release(n) {
    this.reserved -= n;
    fail(this.reserved >= 0, "Negative FIFO reservation");
    this.track();
  }
  adopt(engine, logical, frame) {
    const key = this.key(engine, logical);
    this.drop(key);
    this.frames.set(key, { engine, logical, frame });
    this.track();
  }
  track() {
    fail(this.frames.size <= this.cap && this.reserved <= this.cap && this.frames.size + this.reserved <= this.cap, "Shared frame/FIFO cap exceeded");
    this.peakFrames = Math.max(this.peakFrames, this.frames.size);
    this.peakOutstanding = Math.max(this.peakOutstanding, this.reserved);
    this.peakCombined = Math.max(this.peakCombined, this.frames.size + this.reserved);
  }
  wake() {
    if (this.waking) {
      this.wakeAgain = true;
      return;
    }
    this.waking = true;
    try {
      do {
        this.wakeAgain = false;
        for (const engine of this.engines) engine.pump();
      } while (this.wakeAgain);
    } finally {
      this.waking = false;
    }
  }
  snapshot() {
    return { cap: this.cap, owned: this.frames.size, outstanding: this.reserved, peakFrames: this.peakFrames, peakOutstanding: this.peakOutstanding, peakOwnedPlusOutstanding: this.peakCombined, closed: this.closed };
  }
};
var serial = 0;
var TemporalEngine = class {
  constructor({ config, groups, pool = new FramePool(8), budget = new ResourceBudget(), Decoder = globalThis.VideoDecoder, Chunk = globalThis.EncodedVideoChunk, onEvent = () => {
  }, clock = () => performance.now(), hash = sha256, batchTimeoutMs = 3e3, timers = globalThis }) {
    this.batchTimeoutMs = batchTimeoutMs;
    this.timers = timers;
    this.timer = 0;
    this.id = "e" + ++serial;
    this.config = config;
    this.groups = groups;
    this.pool = pool;
    this.budget = budget;
    this.Decoder = Decoder;
    this.Chunk = Chunk;
    this.onEvent = onEvent;
    this.clock = clock;
    this.hash = hash;
    this.buffers = /* @__PURE__ */ new Map();
    this.queue = [];
    this.wanted = null;
    this.next = null;
    this.currentGroup = null;
    this.epoch = 0;
    this.suspended = false;
    this.disposed = false;
    this.error = null;
    this.outputs = 0;
    this.resets = 0;
    this.flushes = 0;
    this.submissions = 0;
    this.superseded = 0;
    this.decoder = null;
    this.byLogical = /* @__PURE__ */ new Map();
    for (const g of groups) {
      fail(g.chunks.length <= 8 && g.chunks.length > 0, "Invalid group size");
      for (let n = 0; n < g.chunks.length; n++) {
        const c = g.chunks[n];
        fail(c.logical === g.first + n && c.type === (n ? "delta" : "key"), "Invalid dependency/type mapping");
        fail(c.offset >= 0 && c.offset + c.bytes <= g.bytes, "Chunk outside group");
        fail(!this.byLogical.has(c.logical), "Duplicate logical pose");
        this.byLogical.set(c.logical, { g, c });
      }
      fail(g.last === g.chunks.at(-1).logical, "Group endpoint mismatch");
    }
    this.pool.engines.add(this);
  }
  emit(event, extra = {}) {
    this.onEvent({ event, at: this.clock(), engine: this.id, ...extra });
  }
  configure() {
    if (this.decoder) return;
    const epoch = this.epoch;
    this.decoder = new this.Decoder({ output: (f) => this.output(f, epoch), error: (e) => {
      if (epoch === this.epoch && !this.disposed && !this.suspended) this.fail(e);
    } });
    this.decoder.configure(this.config);
    this.emit("configure", { codec: this.config.codec });
  }
  async provide(groupId, buffer, reservation = null) {
    fail(!this.disposed, "Disposed engine");
    const g = this.groups.find((g2) => g2.id === String(groupId));
    fail(g && buffer instanceof ArrayBuffer && buffer.byteLength === g.bytes, "Invalid group response");
    const digest = await this.hash(buffer);
    fail(digest === g.sha256, "Group SHA mismatch");
    if (this.disposed) return false;
    if (this.buffers.has(g.id)) return true;
    if (reservation) {
      fail(reservation.bytes === g.bytes && !reservation.claimed, "Invalidtransferreservation");
      this.budget.transferToResident(g.bytes);
      reservation.claimed = true;
    } else this.budget.add("resident", g.bytes);
    this.buffers.set(g.id, buffer);
    this.emit("provided", { group: g.id, bytes: g.bytes, sha256: digest });
    this.pump();
    return true;
  }
  has(logical) {
    const p = this.byLogical.get(logical);
    return !!p && this.buffers.has(p.g.id);
  }
  frame(logical) {
    return this.pool.get(this, logical);
  }
  seek(logical) {
    fail(this.byLogical.has(logical), "Logical target outside bound source");
    if (this.disposed || this.suspended || this.error) return Promise.resolve({ status: this.error ? "error" : "inactive", logical, error: this.error?.message });
    this.pool.target = logical;
    if (this.wanted?.logical === logical) return this.wanted.promise;
    if (this.wanted) {
      this.wanted.resolve({ status: "superseded", logical: this.wanted.logical });
      this.superseded++;
      this.emit("target-superseded", { logical: this.wanted.logical, next: logical });
    }
    let resolve;
    const promise = new Promise((r) => resolve = r);
    this.wanted = { logical, resolve, promise, at: this.clock() };
    this.emit("target", { logical });
    this.pump();
    return promise;
  }
  cancelPending(reason = "router-change") {
    if (this.wanted) {
      this.wanted.resolve({ status: "superseded", logical: this.wanted.logical, reason });
      this.wanted = null;
      this.superseded++;
    }
  }
  pump() {
    if (this.disposed || this.suspended || this.error || !this.wanted) return;
    const w = this.wanted, f = this.frame(w.logical);
    if (f) {
      this.wanted = null;
      w.resolve({ status: "ready", logical: w.logical, cached: true, elapsed: this.clock() - w.at });
      return;
    }
    if (this.queue.length) return;
    const { g } = this.byLogical.get(w.logical);
    if (!this.buffers.has(g.id)) return;
    const begin = this.currentGroup === g.id && this.next !== null && this.next <= w.logical && this.next >= g.first ? this.next : g.first;
    const chunks = g.chunks.filter((c) => c.logical >= begin && c.logical <= w.logical);
    fail(chunks.length > 0, "Empty dependency prefix");
    if (!this.pool.reserve(chunks.length, w.logical)) return;
    let localReservation = chunks.length, charged = false;
    const submittedBytes = chunks.reduce((n, c) => n + c.bytes, 0), epoch = this.epoch;
    try {
      this.budget.add("submitted", submittedBytes);
      charged = true;
      this.queue = chunks.map((c) => ({ logical: c.logical, timestamp: c.timestamp, bytes: c.bytes, at: this.clock(), epoch }));
      localReservation = 0;
      this.timer = this.timers.setTimeout(() => {
        this.timer = 0;
        if (epoch === this.epoch && this.queue.length) this.fail(Error("No complete batch output within" + this.batchTimeoutMs + "ms"));
      }, this.batchTimeoutMs);
      this.configure();
      this.currentGroup = g.id;
      this.next = chunks.at(-1).logical + 1;
      const buffer = this.buffers.get(g.id);
      for (const c of chunks) {
        if (epoch !== this.epoch || this.error) break;
        this.decoder.decode(new this.Chunk({ type: c.type, timestamp: c.timestamp, duration: c.duration, data: new Uint8Array(buffer, c.offset, c.bytes) }));
        this.submissions++;
        this.emit("submit", { logical: c.logical, type: c.type, queue: this.queue.length, group: g.id });
      }
    } catch (e) {
      if (localReservation) {
        this.pool.release(localReservation);
        if (charged) this.budget.add("submitted", -submittedBytes);
      }
      this.fail(e);
    }
  }
  output(frame, epoch) {
    if (epoch !== this.epoch || this.disposed || this.suspended) {
      frame.close();
      return;
    }
    const expected = this.queue.shift();
    if (!expected) {
      frame.close();
      this.fail(Error("Unsolicited output"));
      return;
    }
    this.pool.release(1);
    this.budget.add("submitted", -expected.bytes);
    try {
      fail(frame.timestamp === expected.timestamp, "Output timestamp/FIFO mismatch");
      fail(frame.displayWidth === this.config.codedWidth && frame.displayHeight === this.config.codedHeight, "Output dimensions mismatch");
      this.pool.adopt(this, expected.logical, frame);
      this.outputs++;
      this.emit("output", { logical: expected.logical, timestamp: frame.timestamp, outputMs: this.clock() - expected.at, remaining: this.queue.length });
    } catch (e) {
      frame.close();
      this.fail(e);
      return;
    }
    if (!this.queue.length) {
      this.timers.clearTimeout(this.timer);
      this.timer = 0;
      this.pump();
      this.pool.wake();
    }
  }
  fail(error) {
    if (this.disposed || this.error) return;
    this.error = error instanceof Error ? error : Error(String(error));
    this.emit("error", { message: this.error.message });
    this.invalidate("error");
    if (this.wanted) {
      this.wanted.resolve({ status: "error", logical: this.wanted.logical, error: this.error.message });
      this.wanted = null;
    }
    this.pool.wake();
  }
  invalidate(reason) {
    this.timers.clearTimeout(this.timer);
    this.timer = 0;
    this.epoch++;
    if (this.queue.length) {
      this.pool.release(this.queue.length);
      this.budget.add("submitted", -this.queue.reduce((n, q) => n + q.bytes, 0));
      this.queue = [];
    }
    if (this.decoder) {
      if (this.decoder.state !== "closed") {
        try {
          this.decoder.reset();
          this.resets++;
          this.emit("reset", { reason });
        } catch {
        }
        this.decoder.close();
      }
      this.decoder = null;
    }
    this.next = null;
    this.currentGroup = null;
    this.pool.clear(this);
  }
  suspend() {
    if (this.disposed || this.suspended) return;
    this.suspended = true;
    this.invalidate("suspend");
    if (this.wanted) {
      this.wanted.resolve({ status: "suspended", logical: this.wanted.logical });
      this.wanted = null;
    }
    this.emit("suspend", { owned: this.pool.list(this).length });
    this.pool.wake();
  }
  resume() {
    if (this.disposed || !this.suspended) return;
    this.suspended = false;
    this.error = null;
    this.emit("resume");
    this.pump();
  }
  dispose() {
    if (this.disposed) return;
    this.suspend();
    this.disposed = true;
    for (const g of this.groups) if (this.buffers.has(g.id)) this.budget.add("resident", -g.bytes);
    this.buffers.clear();
    this.pool.engines.delete(this);
    this.emit("dispose", { owned: this.pool.list(this).length });
  }
  snapshot() {
    return { outputs: this.outputs, submissions: this.submissions, resets: this.resets, flushes: this.flushes, superseded: this.superseded, pending: this.wanted?.logical ?? null, queue: this.queue.length, error: this.error?.message ?? null };
  }
};

// runtime/system-motion/pinned-store.js
var PinnedStore = class {
  constructor({ objects, streams = /* @__PURE__ */ new Map(), routes = [], onStreamReady = () => {
  }, baseURL, budget = new ResourceBudget(), fetcher = fetch, hash = sha256, onProgress = () => {
  }, onEvent = () => {
  } }) {
    Object.assign(this, { objects, streams, routes, onStreamReady, baseURL, budget, fetcher, hash, onProgress, onEvent });
    this.records = /* @__PURE__ */ new Map();
    this.verifiedBytes = 0;
    this.totalBytes = [...objects.values()].reduce((n, o) => n + o.bytes, 0);
    this.disposed = false;
    this.active = 0;
    this.peakActive = 0;
    this.controller = null;
    this.attempts = 0;
    this.readyStreams = /* @__PURE__ */ new Set();
    this.listeners = /* @__PURE__ */ new Set();
  }
  streamReady(id) {
    return this.readyStreams.has(id);
  }
  subscribe(listener) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }
  updateStreams() {
    for (const stream of this.streams.values()) if (!this.readyStreams.has(stream.id) && stream.groups.every((g) => this.records.has(g.objectId))) {
      this.readyStreams.add(stream.id);
      this.onStreamReady(stream.id);
      for (const listener of this.listeners) listener(stream.id);
    }
  }
  async load(signal, { getTarget = () => 0, waitActive = async () => {
  } } = {}) {
    assert(!this.disposed && !this.controller, "Store unavailable");
    const controller = this.controller = new AbortController();
    const abort = () => controller.abort(signal?.reason);
    signal?.addEventListener("abort", abort, { once: true });
    if (signal?.aborted) abort();
    const pending = new Map([...this.objects].filter(([id]) => !this.records.has(id)));
    let error;
    const story = [...new Set(this.routes.map((r) => r.streamId))];
    for (const id of this.streams.keys()) if (!story.includes(id)) story.push(id);
    const next = () => {
      const current = this.routes[Math.max(0, Math.min(this.routes.length - 1, getTarget()))]?.streamId, index = Math.max(0, story.indexOf(current));
      const order = [...story.slice(index), ...story.slice(0, index)];
      for (const id of order) for (const group of this.streams.get(id)?.groups ?? []) {
        const object2 = pending.get(group.objectId);
        if (object2) {
          pending.delete(object2.id);
          return object2;
        }
      }
      const object = pending.values().next().value;
      if (object) pending.delete(object.id);
      return object;
    };
    this.updateStreams();
    const lane = async () => {
      while (pending.size && !controller.signal.aborted) {
        await waitActive(controller.signal);
        if (controller.signal.aborted || !pending.size) break;
        const object = next();
        for (let attempt = 0; attempt < 2; attempt++) {
          try {
            await this.read(object, controller.signal, attempt);
            break;
          } catch (e) {
            if (controller.signal.aborted || attempt) {
              error ??= e;
              controller.abort(e);
              break;
            }
            this.onEvent({ event: "object-retry", object: object.id, message: e.message });
          }
        }
      }
    };
    try {
      await Promise.all([lane(), lane(), lane()]);
      if (error) throw error;
      if (controller.signal.aborted) throw controller.signal.reason ?? Error("Startup canceled");
      assert(this.records.size === this.objects.size, "Incomplete verified package");
    } finally {
      signal?.removeEventListener("abort", abort);
      if (this.controller === controller) this.controller = null;
    }
  }
  async read(object, signal, attempt = 0) {
    const reserve = object.bytes * 3;
    this.budget.add("transfers", reserve);
    this.active++;
    this.peakActive = Math.max(this.peakActive, this.active);
    this.attempts++;
    let committed = false, reader, timer;
    const controller = new AbortController(), abort = () => controller.abort(signal.reason);
    signal.addEventListener("abort", abort, { once: true });
    if (signal.aborted) abort();
    const touch = () => {
      clearTimeout(timer);
      timer = setTimeout(() => controller.abort(Error("No transfer progress for 15 seconds")), 15e3);
    };
    touch();
    try {
      this.onEvent({ event: "object-start", object: object.id, bytes: object.bytes, active: this.active });
      const response = await this.fetcher(new URL(object.url, this.baseURL), { signal: controller.signal, cache: attempt ? "reload" : "default" });
      assert(response.ok, "Object HTTP " + response.status);
      touch();
      const declared = response.headers.get("content-length");
      assert(declared === null || Number(declared) === object.bytes, "Object response length changed");
      assert(response.body?.getReader, "Streaming response unavailable");
      reader = response.body.getReader();
      const buffer = new ArrayBuffer(object.bytes), view = new Uint8Array(buffer);
      let offset = 0;
      for (; ; ) {
        const { value, done } = await reader.read();
        if (done) break;
        assert(!controller.signal.aborted && !this.disposed, "Startup canceled");
        assert(offset + value.byteLength <= object.bytes, "Object overflow");
        view.set(value, offset);
        offset += value.byteLength;
        touch();
        this.onEvent({ event: "object-progress", object: object.id, received: offset, total: object.bytes });
      }
      assert(offset === object.bytes, "Object truncated");
      const digest = await this.hash(buffer);
      assert(!controller.signal.aborted && !this.disposed, "Startup canceled");
      assert(digest === object.sha256, "Object SHA mismatch: " + object.id);
      this.budget.add("transfers", -reserve);
      committed = true;
      this.budget.add("resident", object.bytes);
      this.records.set(object.id, Object.freeze({ id: object.id, sha256: digest, buffer, bytes: object.bytes }));
      this.verifiedBytes += object.bytes;
      this.onEvent({ event: "object-verified", object: object.id, bytes: object.bytes });
      this.updateStreams();
      this.onProgress(this.snapshot());
    } finally {
      clearTimeout(timer);
      signal.removeEventListener("abort", abort);
      if (!committed) this.budget.add("transfers", -reserve);
      this.active--;
      try {
        await reader?.cancel();
      } catch {
      }
      reader?.releaseLock();
    }
  }
  snapshot() {
    return { readyStreams: [...this.readyStreams], verifiedBytes: this.verifiedBytes, totalBytes: this.totalBytes, verifiedObjects: this.records.size, totalObjects: this.objects.size, active: this.active, peakActive: this.peakActive, attempts: this.attempts, budget: this.budget.snapshot() };
  }
  dispose() {
    if (this.disposed) return;
    this.disposed = true;
    this.controller?.abort(Error("Store disposed"));
    for (const record of this.records.values()) this.budget.add("resident", -record.bytes);
    this.records.clear();
    this.readyStreams.clear();
    this.listeners.clear();
    this.verifiedBytes = 0;
  }
};

// runtime/system-motion/journey-engine.js
var PinnedEngine = class extends TemporalEngine {
  constructor({ stream, store, reorderOutputs = false, ...options }) {
    for (const group of stream.groups) {
      const record = store.records.get(group.objectId);
      assert(record?.sha256 === group.sha256 && record.bytes === group.bytes, "Unverified group view");
    }
    super({ ...options, config: stream.config, groups: stream.groups, budget: store.budget });
    this.stream = stream;
    this.store = store;
    this.reorderOutputs = reorderOutputs;
    this.bufferedOutputs = /* @__PURE__ */ new Map();
    this.reorders = 0;
    for (const group of this.groups) this.buffers.set(group.id, store.records.get(group.objectId).buffer);
  }
  provide() {
    throw Error("Pinned engine accepts verified store views only");
  }
  // Native WebKit can deliver no-B outputs out of order. Keep each returned
  // frame inside its existing outstanding lease and accept only timestamps in
  // the current submitted batch. P0 still checks exact FIFO identity/dimensions
  // when committing it; no timestamp is rewritten and no frame is copied.
  output(frame, epoch) {
    if (!this.reorderOutputs || epoch !== this.epoch || this.disposed || this.suspended) return super.output(frame, epoch);
    if (!this.queue.some((q) => q.timestamp === frame.timestamp) || this.bufferedOutputs.has(frame.timestamp)) {
      frame.close();
      this.fail(Error("Unexpected or duplicate native output timestamp"));
      return;
    }
    if (frame.displayWidth !== this.config.codedWidth || frame.displayHeight !== this.config.codedHeight) {
      frame.close();
      this.fail(Error("Output dimensions mismatch"));
      return;
    }
    const expectedTimestamp = this.queue[0].timestamp;
    this.bufferedOutputs.set(frame.timestamp, frame);
    if (frame.timestamp !== expectedTimestamp) {
      this.reorders++;
      this.emit("native-output-buffered", { timestamp: frame.timestamp, expectedTimestamp });
    }
    while (this.queue.length && this.bufferedOutputs.has(this.queue[0].timestamp)) {
      const timestamp = this.queue[0].timestamp, next = this.bufferedOutputs.get(timestamp);
      this.bufferedOutputs.delete(timestamp);
      super.output(next, epoch);
      if (epoch !== this.epoch || this.error || this.suspended || this.disposed) break;
    }
  }
  invalidate(reason) {
    for (const frame of this.bufferedOutputs?.values() ?? []) frame.close();
    this.bufferedOutputs?.clear();
    super.invalidate(reason);
  }
  snapshot() {
    return { ...super.snapshot(), bufferedOutputs: this.bufferedOutputs.size, reorders: this.reorders };
  }
  pump() {
    if (this.router?.suspending || this.router?.suspended && !this.probing || this.router?.disposed) return;
    super.pump();
  }
  dispose() {
    this.buffers.clear();
    super.dispose();
  }
};
var JourneyEngine = class {
  constructor({ bound, store, pool = new FramePool(8), onEvent = () => {
  }, engineOptions = {} }) {
    Object.assign(this, { bound, store, pool, onEvent });
    this.engines = /* @__PURE__ */ new Map();
    this.uniqueEngines = /* @__PURE__ */ new Set();
    this.listeners = /* @__PURE__ */ new Set();
    this.target = null;
    this.suspended = false;
    this.disposed = false;
    this.primed = [];
    const emit = (event) => {
      onEvent(event);
      for (const listener of this.listeners) listener(event);
    };
    try {
      this.emit = emit;
      this.readyStreams = /* @__PURE__ */ new Set();
      this.streamGates = !!engineOptions.streamGates;
      this.engineOptions = engineOptions;
      for (const stream of bound.streams.values()) this.installStream(stream.id);
      this.unsubscribeStore = store.subscribe?.((id) => this.installStream(id));
    } catch (e) {
      this.dispose();
      throw e;
    }
  }
  installStream(id) {
    if (this.disposed || this.engines.has(id)) return;
    const stream = this.bound.streams.get(id);
    if (!stream || !stream.groups.every((g) => this.store.records.has(g.objectId))) return;
    let engine = [...this.uniqueEngines].find((e) => e.config.codec === stream.codec && e.config.codedWidth === stream.width && e.config.codedHeight === stream.height && JSON.stringify([...e.config.description]) === JSON.stringify(stream.description));
    if (engine) {
      for (const group of stream.groups) {
        assert(!engine.groups.some((g) => g.id === group.id), "Duplicate merged group");
        const record = this.store.records.get(group.objectId);
        assert(record?.sha256 === group.sha256 && record.bytes === group.bytes, "Unverified group view");
        for (const chunk of group.chunks) {
          assert(!engine.byLogical.has(chunk.logical), "Duplicate merged decode identity");
          engine.byLogical.set(chunk.logical, { g: group, c: chunk });
        }
        engine.groups.push(group);
        engine.buffers.set(group.id, record.buffer);
      }
    } else {
      engine = new PinnedEngine({ stream, store: this.store, reorderOutputs: this.bound.delivery.source.mediaDevice === "mobile", pool: this.pool, onEvent: this.emit, ...this.engineOptions });
      engine.router = this;
    }
    this.uniqueEngines.add(engine);
    assert(this.uniqueEngines.size <= 4, "Persistent decoder cap exceeded");
    this.engines.set(id, engine);
    if (this.suspended) engine.suspend();
    if (!this.streamGates) this.admitStream(id);
    this.emit({ event: "stream-pinned", stream: id });
  }
  admitStream(id) {
    if (this.disposed || this.readyStreams.has(id)) return;
    this.readyStreams.add(id);
    this.emit({ event: "stream-ready", stream: id });
  }
  readyFor(logical) {
    return this.readyStreams.has(this.bound.routes[logical]?.streamId);
  }
  async prime(onProgress = () => {
  }, waitVisible2 = async () => {
  }) {
    const unique = [...this.uniqueEngines];
    this.primed = [];
    for (let n = 0; n < unique.length; n++) {
      await waitVisible2();
      assert(!this.disposed, "Priming canceled");
      if (this.suspended) this.suspend(false);
      const engine = unique[n], logical = engine.groups[0].first, result = await engine.seek(logical);
      if (result.status === "suspended") {
        this.primed = [];
        n = -1;
        continue;
      }
      assert(result.status === "ready" && engine.frame(logical), "Decoder priming failed: " + engine.stream.id + " " + (result.error ?? result.status));
      this.primed.push(engine.stream.id);
      this.pool.clear(engine);
      this.onEvent({ event: "config-primed", stream: engine.stream.id, decodeFrame: logical, framePool: this.pool.snapshot() });
      onProgress(this.primed.length, unique.length);
    }
  }
  request(logical) {
    assert(Number.isSafeInteger(logical) && this.bound.routes[logical], "Journey target outside source");
    this.target = logical;
    const route = this.bound.routes[logical], engine = this.engines.get(route.streamId);
    for (const candidate of this.uniqueEngines) if (candidate !== engine && !candidate.probing) candidate.cancelPending("journey-stream-change");
    if (engine?.probing) return Promise.resolve({ status: "preparing", logical });
    if (!engine || !this.readyFor(logical)) return Promise.resolve({ status: "unpinned", logical, streamId: route.streamId });
    if (this.suspended || this.disposed) return Promise.resolve({ status: "inactive", logical });
    return engine.seek(route.decodeFrame).then((result) => ({ ...result, logical, decodeFrame: route.decodeFrame, streamId: route.streamId }));
  }
  frame(logical) {
    const route = this.bound.routes[logical];
    return route ? this.engines.get(route.streamId)?.frame(route.decodeFrame) : null;
  }
  subscribe(listener) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }
  candidate(from, target) {
    if (this.frame(target)) return target;
    const lo = Math.min(from ?? 0, target), hi = Math.max(from ?? 0, target);
    let best = null;
    for (let logical = lo; logical <= hi; logical++) {
      if (logical === from || !this.frame(logical)) continue;
      if (best === null || Math.abs(target - logical) < Math.abs(target - best)) best = logical;
    }
    return best;
  }
  suspend(value = true, { force = false } = {}) {
    if (this.disposed || value === this.suspended && !force) return;
    this.suspended = value;
    this.suspending = value;
    try {
      for (const engine of this.uniqueEngines) value ? engine.suspend() : engine.resume();
    } finally {
      this.suspending = false;
    }
  }
  snapshot() {
    return { readyStreams: [...this.readyStreams], pinnedStreams: [...this.engines.keys()], primed: [...this.primed], target: this.target, suspended: this.suspended, frames: this.pool.snapshot(), compressed: this.store.budget.snapshot(), liveDecoders: [...this.uniqueEngines].filter((e) => e.decoder?.state === "configured").length, streams: Object.fromEntries([...this.engines].map(([id, e]) => [id, e.snapshot()])) };
  }
  dispose() {
    if (this.disposed) return;
    this.disposed = true;
    this.unsubscribeStore?.();
    for (const engine of this.uniqueEngines) engine.dispose();
    this.engines.clear();
    this.uniqueEngines.clear();
    this.listeners.clear();
  }
};

// runtime/system-motion/startup.js
async function verifiedJSON(url, expected, signal, fetcher = fetch) {
  const response = await fetcher(url, { signal, cache: "no-store" });
  assert(response.ok, "Metadata HTTP " + response.status);
  const bytes = await response.arrayBuffer();
  assert(bytes.byteLength <= 4 * 1024 * 1024, "Metadata too large");
  assert(await sha256(bytes) === expected, "Metadata SHA mismatch");
  assert(!signal?.aborted, "Metadata canceled");
  return JSON.parse(new TextDecoder().decode(bytes));
}
async function createPreparation({ manifestURL, manifestSHA, signal, fetcher = fetch, Decoder = globalThis.VideoDecoder, onProgress = () => {
}, onEvent = () => {
}, engineOptions = {}, getProgress = () => 0, getTarget = null }) {
  assert(globalThis.isSecureContext !== false && Decoder, "Direct decoding unavailable");
  const delivery = await verifiedJSON(manifestURL, manifestSHA, signal, fetcher), source = delivery.source;
  const presentation = await verifiedJSON(new URL(source.presentationManifestURL, manifestURL), source.presentationManifestSHA256, signal, fetcher);
  const indexes = new Map(await Promise.all(source.indexes.map(async (reference) => [reference.clipId, await verifiedJSON(new URL(reference.url, manifestURL), reference.sha256, signal, fetcher)])));
  const bound = validateJourney(delivery, presentation, indexes);
  for (const stream of bound.streams.values()) {
    const support = await Decoder.isConfigSupported(stream.config);
    if (!support.supported) {
      const error = Error("Unsupported decoder configuration: " + stream.id);
      error.code = "capability";
      throw error;
    }
  }
  assert(!signal.aborted, "Startup canceled");
  const store = new PinnedStore({ objects: bound.objects, streams: bound.streams, routes: bound.routes, baseURL: manifestURL, fetcher, onEvent, onProgress: (progress) => onProgress({ phase: "verifying", ...progress }) });
  let disposed = false;
  const playbackFrames = bound.variant.frames.slice(0, bound.routes.length);
  const engine = new JourneyEngine({ bound, store, onEvent, engineOptions: { Decoder, ...engineOptions, streamGates: true } });
  return {
    bound,
    store,
    engine,
    earlyStart: true,
    async prepare(signal2, waitVisible2 = async () => {
    }) {
      const cancel = () => {
        engine.dispose();
        store.controller?.abort(signal2.reason);
      };
      signal2.addEventListener("abort", cancel, { once: true });
      let probes = Promise.resolve(), probeError = null;
      const probe = (id) => {
        probes = probes.then(async () => {
          const candidate = engine.engines.get(id);
          assert(candidate, "Pinned stream engine missing");
          for (; ; ) {
            await waitVisible2(signal2);
            assert(!signal2.aborted && !disposed, "Startup canceled");
            candidate.probing = true;
            candidate.resume();
            const groups = bound.streams.get(id).groups;
            const worst = groups.reduce((a, b) => b.chunks.length > a.chunks.length || b.chunks.length === a.chunks.length && b.chunks.reduce((n, c) => n + c.bytes, 0) > a.chunks.reduce((n, c) => n + c.bytes, 0) ? b : a);
            const temporal = worst.chunks.length > 1, group = temporal ? worst : groups[0], logical = group.last;
            const began = performance.now();
            let elapsed = 0;
            const stopProbe = engine.subscribe((event) => {
              if (event.engine === candidate.id && event.event === "output" && event.logical >= group.first && event.logical <= group.last) elapsed = Math.max(elapsed, event.outputMs);
            });
            let result;
            try {
              result = await candidate.seek(logical);
            } finally {
              stopProbe();
            }
            const wallMs = performance.now() - began;
            candidate.probing = false;
            if (result.status === "suspended") {
              continue;
            }
            assert(result.status === "ready" && candidate.frame(logical), "Stream decoder preparation failed: " + id);
            engine.pool.clear(candidate);
            if (engine.suspended) candidate.suspend();
            onEvent({ event: "prefix-probe", stream: id, group: group.id, chunks: group.chunks.length, elapsed, wallMs, limitMs: temporal ? 100 : null });
            if (temporal && elapsed > 100) {
              const error = Error("This device could not prepare motion within the startup capability limit");
              error.code = "capability";
              throw error;
            }
            engine.primed.push(id);
            engine.admitStream(id);
            onProgress({ phase: "verifying", stream: id, ...store.snapshot() });
            break;
          }
        }).catch((error) => {
          probeError ??= error;
          store.controller?.abort(error);
        });
      };
      const unsubscribe = store.subscribe(probe);
      try {
        assert(!disposed && !signal2.aborted, "Startup canceled");
        await store.load(signal2, { waitActive: waitVisible2, getTarget: () => getTarget ? getTarget() : nearestFrame(playbackFrames, getProgress()) });
        await probes;
        if (probeError) throw probeError;
        assert(!signal2.aborted, "Startup canceled");
        onEvent({ event: "journey-prepared", streams: engine.primed.length, objects: store.records.size, bytes: store.verifiedBytes, ...engine.snapshot() });
        return this;
      } catch (error) {
        await probes;
        throw probeError ?? error;
      } finally {
        unsubscribe();
        signal2.removeEventListener("abort", cancel);
      }
    },
    dispose() {
      if (disposed) return;
      disposed = true;
      engine?.dispose();
      store.dispose();
    }
  };
}

// runtime/system-motion/gate-state.js
function waitVisible(signal, doc = document) {
  if (signal.aborted) return Promise.reject(signal.reason ?? Error("Canceled"));
  if (!doc.hidden) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const cleanup = () => {
      doc.removeEventListener("visibilitychange", change);
      signal.removeEventListener("abort", abort);
    };
    const change = () => {
      if (!doc.hidden) {
        cleanup();
        resolve();
      }
    };
    const abort = () => {
      cleanup();
      reject(signal.reason ?? Error("Canceled"));
    };
    doc.addEventListener("visibilitychange", change);
    signal.addEventListener("abort", abort, { once: true });
  });
}

// runtime/system-progressive/zones.js
var zones = [
  { id: "H0", kind: "hold", start: 0, end: TIMING.introOut[0], poster: "form" },
  { id: "L1", kind: "leg", start: TIMING.introOut[0], end: 0.22 },
  { id: "R", kind: "leg", start: 0.22, end: 0.34, poster: "refill" },
  { id: "L1b", kind: "leg", start: 0.34, end: TIMING.partnerTwist[1] },
  { id: "H1", kind: "hold", start: TIMING.partnerTwist[1], end: TIMING.utilityOut[0], poster: "utility" },
  { id: "L2", kind: "leg", start: TIMING.utilityOut[0], end: BEATS.functionStart },
  { id: "H2", kind: "hold", start: BEATS.functionStart, end: FILM_END, poster: "function" }
];
var zoneAt = (p) => zones.find((z) => p < z.end) || zones.at(-1);

// runtime/system-progressive/parity.js
var equal = (a, b) => JSON.stringify(a) === JSON.stringify(b);
function createParity(bound) {
  const equivalent = (from, to, compact) => {
    const variant = bound.presentation.variants[compact ? "mobile" : "desktop"], a = bound.routes[from], b = bound.routes[to];
    if (!a || !b) return false;
    return a.clipId === b.clipId && a.streamId === b.streamId && a.decodeFrame === b.decodeFrame && a.boundOriginalEncodedFrame === b.boundOriginalEncodedFrame && equal(variant.frames[from].sourceFrame, variant.frames[to].sourceFrame) && equal(variant.frames[from].presentation, variant.frames[to].presentation) && equal(framePlacement(variant.frames[from].progress, compact, 1), framePlacement(variant.frames[to].progress, compact, 1));
  };
  const parity = function(p, compact, target) {
    const zone = p === TIMING.utilityOut[0] ? { kind: "hold", poster: "utility" } : zoneAt(p), variant = bound.presentation.variants[compact ? "mobile" : "desktop"];
    if (target !== nearestFrame(variant.frames, p) || zone.kind !== "hold") return false;
    if (zone.poster === "empty") return p >= TIMING.black && variant.frames[target].progress >= TIMING.black;
    const reference = variant.posters[zone.poster].frame, route = bound.routes[target], posterRoute = bound.routes[reference];
    return route.clipId === posterRoute.clipId && route.streamId === posterRoute.streamId && route.decodeFrame === posterRoute.decodeFrame && route.boundOriginalEncodedFrame === posterRoute.boundOriginalEncodedFrame && equal(variant.frames[target].sourceFrame, variant.frames[reference].sourceFrame) && equal(variant.frames[target].presentation, variant.frames[reference].presentation) && equal(framePlacement(variant.frames[target].progress, compact, 1), framePlacement(variant.frames[reference].progress, compact, 1));
  };
  parity.equivalent = equivalent;
  return parity;
}

// runtime/system-progressive/bridge.js
var BRIDGE_KEY = Symbol.for("boxbox.system.progressive");
function staticFallback(doc = document) {
  doc.body.classList.remove("scroll-enabled");
  doc.body.classList.add("system-static");
}

// runtime/system-progressive/lifecycle.js
function createMountOwner(create) {
  let active = null;
  return { mount() {
    if (active) return active.dispose;
    const owner2 = { dispose: null };
    const cleanup = create();
    let disposed = false;
    owner2.dispose = () => {
      if (disposed) return;
      disposed = true;
      if (active === owner2) active = null;
      cleanup?.();
    };
    active = owner2;
    return owner2.dispose;
  }, dispose() {
    active?.dispose();
  } };
}

// runtime/system-progressive/telemetry.js
function emitTelemetry(event, detail = {}) {
  if (new URLSearchParams(location.search).get("motion-debug") === "1") document.dispatchEvent(new CustomEvent("boxbox:film-telemetry", { detail: { ...detail, event, at: performance.now() } }));
}

// runtime/system-progressive/entry.js
var fetchMedia = (url, options) => fetch(url, options);
function mount() {
  const bridge = window[BRIDGE_KEY];
  if (!bridge) {
    staticFallback();
    return () => {
    };
  }
  const study = bridge.mount(), config = document.querySelector("[data-system-config]");
  if (!study || !config) return () => {
  };
  const mobileMedia = window.innerWidth <= 760 && !!config.dataset.mobileJourneyManifestUrl;
  const manifestURL = new URL(mobileMedia ? config.dataset.mobileJourneyManifestUrl : config.dataset.journeyManifestUrl, location.href), manifestSHA = mobileMedia ? "ca8a40d2d37dab44809c35c4f9e3a4ed8a2e3e3d43c34f0ec70f26caff28a19a" : "0c69287de90918d8d9153d200f628a032591e3129d3fa777589af3a28ac401af";
  if (config.dataset.mobileJourneyManifestUrl) {
    const manifestBytes = mobileMedia ? 5181070 : 22837307;
    if ((mobileMedia ? config.dataset.mobileJourneyManifestSha : config.dataset.journeyManifestSha) !== manifestSHA || Number(mobileMedia ? config.dataset.mobileJourneyManifestBytes : config.dataset.journeyBytes) !== manifestBytes) throw Error("System delivery configuration changed");
  }
  const lifetime = new AbortController();
  let disposed = false, installed = false;
  const waitEnvironment = async (signal) => {
    await waitVisible(signal);
    if (!study.reduced) return;
    await new Promise((resolve, reject) => {
      const cleanup = () => {
        preference.removeEventListener("change", change);
        constrained.removeEventListener("change", change);
        window.removeEventListener?.("resize", change);
        signal.removeEventListener("abort", abort);
      };
      const change = () => queueMicrotask(() => {
        if (!study.reduced) {
          cleanup();
          resolve();
        }
      }), abort = () => {
        cleanup();
        reject(signal.reason);
      };
      if (signal.aborted) {
        abort();
        return;
      }
      preference.addEventListener("change", change);
      constrained.addEventListener("change", change);
      window.addEventListener?.("resize", change);
      signal.addEventListener("abort", abort, { once: true });
    });
  };
  const supervisor = createReadiness({
    createPreparation,
    options: { manifestURL, manifestSHA, fetcher: fetchMedia, Decoder: globalThis.VideoDecoder, getProgress: () => study.progress ?? 0, onEvent: (detail) => emitTelemetry(detail.event, { category: "preparation", ...detail }) },
    settlePosters: async () => {
      const results = await Promise.allSettled([...document.querySelectorAll(".scene-poster img")].map((img) => img.decode()));
      emitTelemetry("posters-settled", { results: results.map((r) => r.status) });
    },
    afterPaint: afterFirstPaint,
    waitVisible: waitEnvironment,
    saveData: !!navigator.connection?.saveData,
    supported: !!globalThis.VideoDecoder && globalThis.isSecureContext,
    onState: (state) => emitTelemetry("readiness", state),
    onError: (error) => study.fault(error),
    onPrepared: async (pack, signal) => {
      if (disposed || signal.aborted) throw signal.reason ?? Error("System section removed");
      pack.engine.suspend(true);
      installPreparedJourney(pack);
      installed = true;
      await study.attachFilm({ createSystemFilm: createDirectSystemFilm, getPlayer: getCurrentPlayer, parity: createParity(pack.bound) });
    }
  });
  Object.defineProperty(study, "readiness", { configurable: true, get: () => supervisor.snapshot() });
  if (!study.reduced) supervisor.start();
  const preference = matchMedia("(prefers-reduced-motion: reduce)"), constrained = matchMedia("(orientation: landscape) and (max-height: 500px), (max-width: 760px) and (max-height: 620px)");
  const environmentChanged = () => {
    if (study.reduced) supervisor.pack?.engine?.suspend(true, { force: true });
    else supervisor.start();
  };
  for (const media of [preference, constrained]) media.addEventListener("change", environmentChanged, { signal: lifetime.signal });
  window.addEventListener?.("resize", environmentChanged, { signal: lifetime.signal });
  document.addEventListener("visibilitychange", () => {
    if (document.hidden) supervisor.pack?.engine?.suspend(true, { force: true });
  }, { signal: lifetime.signal });
  return () => {
    if (disposed) return;
    disposed = true;
    lifetime.abort();
    supervisor.dispose();
    bridge.dispose(study);
    if (installed) {
      releasePreparedJourney();
      installed = false;
    }
  };
}
var owner = createMountOwner(mount);
var mountSystemProgressive = () => owner.mount();

// runtime/system.js
var dispose = mountSystemProgressive();
var affectsSystem = (event) => event.target?.querySelector?.(".system-story,#next-chapter,#be-ready,#living-lab");
document.addEventListener("shopify:section:unload", (event) => {
  if (affectsSystem(event)) dispose();
});
document.addEventListener("shopify:section:load", (event) => {
  if (affectsSystem(event)) {
    dispose();
    dispose = mountSystemProgressive();
  }
});
